"use client";

import { useGameLocale, useT } from "@/lib/i18n";
import { useState, useEffect, useRef } from "react";
import { Link } from "@/i18n/navigation";
import type { Power } from "@/lib/api/types";
import { cachedFetch } from "@/lib/fetch-cache";
import SearchFilter from "@/app/components/SearchFilter";
import RichDescription from "@/app/components/RichDescription";
import { useChannel, useBetaPrefix } from "@/lib/api/prefix.client";
import { useBetaAdditions } from "@/lib/use-beta-additions";
import BetaBadge from "@/app/components/BetaBadge";
import { imageUrl } from "@/lib/image-url";

const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";

const typeColors: Record<string, string> = {
  Buff: "border-success/40 text-success",
  Debuff: "border-danger/40 text-danger",
  None: "border-line-strong/40 text-fg-muted",
};

const typeOptions = [
  { label: "Buff", value: "Buff" },
  { label: "Debuff", value: "Debuff" },
];

const stackOptions = [
  { label: "Counter", value: "Counter" },
  { label: "Single", value: "Single" },
];

export default function PowersClient({
  initialPowers,
}: {
  initialPowers: Power[];
}) {
  const bp = useBetaPrefix();
  const [powers, setPowers] = useState<Power[]>(initialPowers);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [stackType, setStackType] = useState("");
  const lang = useGameLocale();
  const t = useT();
  const channel = useChannel();
  const betaAdditions = useBetaAdditions<Power>("powers", lang);
  const initialRender = useRef(true);

  useEffect(() => {
    // Skip the first fetch if we have server data and lang is English with
    // no filters. Never skip on the beta channel: the server data is the
    // stable catalog, and cachedFetch appends channel=beta on /beta paths.
    if (initialRender.current) {
      initialRender.current = false;
      if (
        channel !== "beta" &&
        lang === "eng" &&
        !type &&
        !stackType &&
        !search &&
        initialPowers.length > 0
      ) {
        return;
      }
    }
    const params = new URLSearchParams();
    if (type) params.set("type", type);
    if (stackType) params.set("stack_type", stackType);
    if (search) params.set("search", search);
    params.set("lang", lang);
    cachedFetch<Power[]>(`${API}/api/powers?${params}`).then(setPowers);
  }, [type, search, stackType, lang, channel]);

  // Beta-only powers join the stable list (the regular filters run
  // server-side, so apply them locally to the additions).
  const betaIds = new Set(betaAdditions.map((p) => p.id));
  const merged = [
    ...powers.filter((p) => !betaIds.has(p.id)),
    ...betaAdditions.filter(
      (p) =>
        (!type || p.type === type) &&
        (!stackType || p.stack_type === stackType) &&
        (!search || p.name.toLowerCase().includes(search.toLowerCase())),
    ),
  ];

  return (
    <>
      <SearchFilter
        search={search}
        onSearchChange={setSearch}
        placeholder={t("Search powers...")}
        resultCount={merged.length}
        filters={[
          {
            label: "Any Type",
            name: "Type",
            value: type,
            options: typeOptions,
            onChange: setType,
          },
          {
            label: "Any Stack Type",
            name: "Stack Type",
            value: stackType,
            options: stackOptions,
            onChange: setStackType,
          },
        ]}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {merged.map((power) => {
          const style =
            typeColors[power.type] ||
            "border-[var(--border-subtle)] text-fg-muted";
          return (
            <Link
              prefetch={false}
              key={power.id}
              href={
                betaIds.has(power.id)
                  ? `${bp}/beta/powers/${power.id.toLowerCase()}`
                  : `${bp}/powers/${power.id.toLowerCase()}`
              }
              className={`bg-[var(--bg-card)] rounded-lg border ${style.split(" ")[0]} p-4 hover:bg-[var(--bg-card-hover)] transition-all`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  {power.image_url && (
                    <img
                      src={imageUrl(power.image_url)}
                      alt=""
                      width={32}
                      height={32}
                      loading="lazy"
                      className="w-8 h-8 object-contain flex-shrink-0"
                      crossOrigin="anonymous"
                    />
                  )}
                  <h3 className="font-semibold text-[var(--text-primary)] leading-tight flex items-center gap-1.5">
                    {power.name}
                    {betaIds.has(power.id) && <BetaBadge />}
                  </h3>
                </div>
                <div className="flex gap-1.5 flex-shrink-0 ml-2">
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded ${style.split(" ").slice(1).join(" ")} bg-[var(--bg-primary)]`}
                  >
                    {t(power.type)}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded text-[var(--text-muted)] bg-[var(--bg-primary)]">
                    {t(power.stack_type)}
                  </span>
                </div>
              </div>
              {power.description && (
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                  <RichDescription text={power.description} />
                </p>
              )}
            </Link>
          );
        })}
      </div>
    </>
  );
}
